import { useCallback, useEffect, useState } from 'react';
import './dashShared.css';
import './CSMain.css';

import { CSSummaryDashboard } from './CS_Elements/CSSummaryDashboard';
import { CSGASummary } from './CS_Elements/CSGASummary';
import { CSHCNonHC360 } from './CS_Elements/CSHCNonHC360';
import { CSConsolidatedMaster } from './CS_Elements/CSConsolidatedMaster';
import { DevOverlay } from '../components/DevOverlay/DevOverlay';
import { API_URL } from '../config/api';
import { readApiJson, PERMISSION_DENIED_MESSAGE } from '../utils/apiErrors';
import { optionsBySpend } from '../utils/filterOptionsBySpend';
import { MultiSelectFilter } from '../components/MultiSelectFilter/MultiSelectFilter';

const TABS = [
    { id: 'dashboard', label: 'Dashboard', dot: '#059669', component: 'CSSummaryDashboard' },
    { id: 'ga-summary', label: 'CS Summary', dot: '#d97706', component: 'CSGASummary' },
    { id: 'hc-nonhc-360', label: 'HC & Non-HC 360', dot: '#6d28d9', component: 'CSHCNonHC360' },
    { id: 'consolidated', label: 'Manage Master', dot: '#be185d', component: 'CSConsolidatedMaster', dataManagerOnly: true },
];

// Both HC and Non-HC data feed the Consolidated Master tab, just against
// different API roots — one hook instance per section keeps CSDashboard
// itself simple instead of duplicating this block twice inline.
function useGaSection(apiRoot, onDataLoaded) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}${apiRoot}/dashboard/`, { credentials: 'include' });
            setData(await readApiJson(res, 'The dashboard API could not be loaded.'));
            onDataLoaded?.();
        } catch (err) {
            setMessage(err.message === PERMISSION_DENIED_MESSAGE ? err.message : `${err.message} Check that Django is running on port 8000.`);
        } finally {
            setLoading(false);
        }
    }, [apiRoot, onDataLoaded]);

    useEffect(() => { load(); }, [load]);

    return { data, loading, message, load };
}

function CSDashboard({ user, onDataLoaded }) {
    const [tab, setTab] = useState('dashboard');
    const canManageData = user?.isSuperuser || user?.access?.includes('data_manager');
    const visibleTabs = TABS.filter(t => !t.dataManagerOnly || canManageData);
    // A user who lost data-manager access (or opened this with a stale tab
    // param) shouldn't land on a tab that's no longer in the nav.
    useEffect(() => {
        if (tab === 'consolidated' && !canManageData) setTab('dashboard');
    }, [tab, canManageData]);

    const hc = useGaSection('/api/cs', onDataLoaded);
    const nonhc = useGaSection('/api/cs/nonhc', onDataLoaded);
    const [consolidatedUploading, setConsolidatedUploading] = useState(false);
    const [consolidatedMessage, setConsolidatedMessage] = useState('');

    // Global filters — shared by the Dashboard and G&A Summary tabs only
    // (Manage Master shows raw records and isn't scoped by these). Picking a
    // filter once applies everywhere it's meaningful: KPI tiles, tables,
    // charts, and every deep-dive modal underneath them.
    // Every filter below is multi-select (array of selected values) except
    // the From/To custom date range, which stays a single value each. Empty
    // array means "no restriction" (same meaning as the old '' default) for
    // every filter except Fiscal Year, which always needs at least one mode
    // selected to know what to show — it defaults to ['current'] instead and
    // the UI doesn't allow emptying it out (see the Fiscal Year filter below).
    const [department, setDepartment] = useState([]);
    const [yearMode, setYearMode] = useState(['current']);
    const [quarter, setQuarter] = useState([]);
    const [period, setPeriod] = useState([]);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [category, setCategory] = useState([]);
    // Status/HC Type/Country (Headcount-side) and Sub-Category (Non-HC-side)
    // — every option list below is read straight from the uploaded data
    // (hc.data.filters / nonhc.data.filters, already fetched for Manage
    // Master), so a newly-imported workbook's values show up here with no
    // hardcoded list to maintain.
    const [hcType, setHcType] = useState([]);
    const [status, setStatus] = useState([]);
    const [country, setCountry] = useState([]);
    const [subCategory, setSubCategory] = useState([]);
    const [vendorName, setVendorName] = useState([]);
    // Resolved from the Dashboard tab's own fetched data once it loads — the
    // literal fiscal years (e.g. 2026/2025) aren't known here otherwise.
    const [years, setYears] = useState({ current: '', prior: '' });

    // Filter bar is always visible (matches ARR/Pipeline/AR/Legal) — every
    // change applies immediately, no separate Apply step.
    const resetFilters = () => {
        setDepartment([]); setYearMode(['current']); setQuarter([]); setPeriod([]);
        setCustomStart(''); setCustomEnd(''); setCategory([]);
        setHcType([]); setStatus([]); setCountry([]); setSubCategory([]); setVendorName([]);
    };

    const departmentOptions = Array.from(new Set([
        ...(hc.data?.filters?.category || []),
        ...(nonhc.data?.filters?.category || []),
    ])).sort();
    const hcTypeOptions = hc.data?.filters?.hc_type || [];
    const statusOptions = hc.data?.filters?.status || [];
    const countryOptions = hc.data?.filters?.country || [];
    // Sub-Category is structurally a Non-HC-only dimension — Headcount
    // records carry no sub_category field at all, so when Category is
    // narrowed to Headcount the dropdown's option list is forced empty
    // (shows only "All Sub-Categories", nothing selectable) instead of
    // still listing every Non-HC sub-category as if it applied.
    // Ranked by total FY spend (largest first) rather than alphabetically,
    // and computed from the raw records (not nonhc.data.filters) so Vendor
    // Name can be scoped to whichever Sub-Category is currently selected.
    const nonhcRecords = nonhc.data?.records || [];
    // "Headcount only selected" means Non-HC-only fields (Sub-Category,
    // Vendor Name) don't apply — array-aware: true only when Category has a
    // selection AND Non-Headcount isn't one of the picks.
    const nonhcExcluded = category.length > 0 && !category.includes('nonhc');
    const subCategoryOptions = nonhcExcluded ? [] : optionsBySpend(nonhcRecords, 'sub_category');
    // Vendor Name is the same Non-HC-only dimension Sub-Category already is
    // (Headcount records have no vendor name to filter) — scoped to the
    // selected Sub-Category so picking one only lists vendors under it.
    const vendorNameOptions = nonhcExcluded ? [] : optionsBySpend(nonhcRecords, 'name', 'sub_category', subCategory);

    // Custom date range takes priority over Quarter when both would apply.
    const customRange = customStart && customEnd ? { start: customStart, end: customEnd } : null;
    const effectiveQuarter = customRange ? [] : quarter;

    const consolidatedUpload = async (file, mode, table) => {
        if (!file) return;
        setConsolidatedUploading(true); setConsolidatedMessage('');
        const fd = new FormData();
        fd.append('file', file);
        fd.append('mode', mode);
        if (table) fd.append('table', table);
        try {
            const res = await fetch(`${API_URL}/api/cs/import-consolidated/`, { method: 'POST', body: fd, credentials: 'include' });
            const result = await readApiJson(res, 'Import failed.');
            setConsolidatedMessage(result.message);
            await Promise.all([hc.load(), nonhc.load()]);
        } catch (err) {
            setConsolidatedMessage(err.message);
        } finally {
            setConsolidatedUploading(false);
        }
    };

    return (
        <div className="dash-shell cs-dashboard esd">

            {/* ── Sub-navigation ── */}
            <DevOverlay name="TabNavigation">
                <div className="dash-subnav">
                    <div className="dash-tabs">
                        {visibleTabs.map(t => (
                            <button
                                key={t.id}
                                data-component={t.component}
                                className={`dash-tab${tab === t.id ? ' active' : ''}`}
                                onClick={() => setTab(t.id)}
                            >
                                <span
                                    className="dash-tab-dot"
                                    style={{ background: tab === t.id ? t.dot : 'transparent', border: `2px solid ${t.dot}` }}
                                />
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            </DevOverlay>

            {/* ── Filter bar — always visible, applies immediately (matches
                 ARR/Pipeline/AR/Legal). Dashboard, G&A Summary & HC/Non-HC
                 360 only; Manage Master shows raw records and isn't scoped
                 by these. ── */}
            {(tab === 'dashboard' || tab === 'ga-summary' || tab === 'hc-nonhc-360') && (
                <DevOverlay name="FilterBar">
                    <div className="dash-filterbar">
                        <div className="dash-fb-rows">
                        <div className="dash-fb-row">
                        <MultiSelectFilter label="Department Sub-Category" options={departmentOptions} selected={department} onChange={setDepartment} />

                        <MultiSelectFilter
                            label="Fiscal Year"
                            options={[
                                { value: 'current', label: years.current ? `FY ${years.current}` : 'Current Year' },
                                { value: 'prior', label: years.prior ? `FY ${years.prior}` : 'Prior Year' },
                            ]}
                            selected={yearMode}
                            onChange={vals => setYearMode(vals.length ? vals : ['current'])}
                        />

                        <MultiSelectFilter
                            label="Period"
                            options={[{ value: 'fy', label: 'Full Year' }, { value: 'ytd', label: 'Year to Date' }]}
                            selected={period}
                            onChange={setPeriod}
                        />

                        <MultiSelectFilter
                            label="Quarter"
                            options={['Q1', 'Q2', 'Q3', 'Q4']}
                            selected={effectiveQuarter}
                            onChange={vals => { setQuarter(vals); if (vals.length) { setCustomStart(''); setCustomEnd(''); } }}
                            disabled={!!customRange}
                        />

                        <label className="dash-filter-group">
                            <span className="dash-filter-label">From</span>
                            <input
                                className="dash-filter-month" type="month" value={customStart}
                                onChange={e => { setCustomStart(e.target.value); setQuarter([]); }}
                            />
                        </label>
                        <label className="dash-filter-group">
                            <span className="dash-filter-label">To</span>
                            <input
                                className="dash-filter-month" type="month" value={customEnd}
                                onChange={e => { setCustomEnd(e.target.value); setQuarter([]); }}
                            />
                        </label>

                        <MultiSelectFilter
                            label="Category"
                            options={[{ value: 'hc', label: 'Headcount' }, { value: 'nonhc', label: 'Non-Headcount' }]}
                            selected={category}
                            onChange={vals => {
                                setCategory(vals);
                                if (vals.length && !vals.includes('nonhc')) { setSubCategory([]); setVendorName([]); }
                            }}
                            title="No selection = Total (Headcount + Non-Headcount combined)"
                        />
                        </div>

                        <div className="dash-fb-row">
                        <MultiSelectFilter label="Status" options={statusOptions} selected={status} onChange={setStatus} />

                        <MultiSelectFilter label="HC Category" options={hcTypeOptions} selected={hcType} onChange={setHcType} />

                        <MultiSelectFilter label="Country" options={countryOptions} selected={country} onChange={setCountry} />

                        <MultiSelectFilter
                            label="Sub-Category(Non-HC)"
                            options={subCategoryOptions}
                            selected={subCategory}
                            disabled={nonhcExcluded}
                            title={nonhcExcluded
                                ? 'Sub-Category does not apply to Headcount-only records.'
                                : 'Sub-Category only applies to Non-Headcount records — picking any switches Category to Non-Headcount so the KPIs aren\'t silently blended with unfiltered Headcount spend.'}
                            onChange={vals => {
                                setSubCategory(vals);
                                setVendorName([]);
                                if (vals.length) setCategory(['nonhc']);
                            }}
                        />

                        <MultiSelectFilter
                            label="Vendor Name"
                            options={vendorNameOptions}
                            selected={vendorName}
                            disabled={nonhcExcluded}
                            title={nonhcExcluded
                                ? 'Vendor Name does not apply to Headcount-only records.'
                                : 'Vendor Name only applies to Non-Headcount records — picking any switches Category to Non-Headcount so the KPIs aren\'t silently blended with unfiltered Headcount spend.'}
                            onChange={vals => {
                                setVendorName(vals);
                                if (vals.length) setCategory(['nonhc']);
                            }}
                        />

                        <button className="dash-filter-reset" onClick={resetFilters}>↩ Reset</button>
                        </div>
                        </div>
                    </div>
                </DevOverlay>
            )}

            {/* ── Content ── */}
            {tab === 'dashboard' && (
                <div className="dash-content">
                    <DevOverlay name="CSSummaryDashboard">
                        <CSSummaryDashboard
                            department={department} quarter={effectiveQuarter} customRange={customRange} period={period}
                            category={category} yearMode={yearMode} onYears={setYears}
                            hcType={hcType} status={status} country={country} subCategory={subCategory} vendorName={vendorName}
                        />
                    </DevOverlay>
                </div>
            )}
            {tab === 'ga-summary' && (
                <div className="dash-content">
                    <DevOverlay name="CSGASummary">
                        <CSGASummary
                            department={department} quarter={effectiveQuarter} customRange={customRange} period={period}
                            category={category} yearMode={yearMode}
                            hcType={hcType} status={status} country={country} subCategory={subCategory} vendorName={vendorName}
                        />
                    </DevOverlay>
                </div>
            )}
            {tab === 'hc-nonhc-360' && (
                <div className="dash-content">
                    <DevOverlay name="CSHCNonHC360">
                        <CSHCNonHC360
                            department={department} quarter={effectiveQuarter} customRange={customRange} period={period} yearMode={yearMode} category={category}
                            hcType={hcType} status={status} country={country} subCategory={subCategory} vendorName={vendorName}
                        />
                    </DevOverlay>
                </div>
            )}
            {tab === 'consolidated' && canManageData && (
                <div className="dash-content">
                    {consolidatedMessage && <div className="dash-notice">{consolidatedMessage}</div>}
                    <DevOverlay name="CSConsolidatedMaster">
                        <CSConsolidatedMaster
                            hcData={hc.data}
                            nonhcData={nonhc.data}
                            upload={consolidatedUpload}
                            uploading={consolidatedUploading}
                            canManageData={canManageData}
                        />
                    </DevOverlay>
                </div>
            )}
        </div>
    );
}

export default CSDashboard;
